import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft } from "@untitledui/icons";
import { useEffect, useState } from "react";
import {
  fetchOpenCollaborationCreators,
  openCollaborationCreatorsKey,
  useOpenCollaborationDetail,
} from "@/api/collaborations";
import { useScopedMerchantClient } from "@/api/scoped-merchant-client";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMerchant } from "@/providers/MerchantProvider";
import type { openCollaborationDetailRoute } from "@/router";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { CollaborationCreatorsTable } from "./collaboration-creators-table";
import type { CreatorStatusTab } from "./collaboration-table-columns";
import { CollaborationInsightsPanel } from "./insights";
import { OpenCollaborationDetailHeader } from "./open-collaboration-detail-header";

/**
 * Open Collaboration Detail Page
 *
 * Displays detailed information about a specific open collaboration including:
 * - Header with product image, name, type, stock, commission rate
 * - Tabs for filtering creators by status
 * - Table of creators in the collaboration with search and actions
 *
 * This page is accessed by clicking on a collaboration row in the Open Collaborations table.
 */
export const OpenCollaborationDetail = () => {
  const { collaborationId } = useParams({ strict: false }) as { collaborationId: string };
  const navigate = useNavigate() as ReturnType<(typeof openCollaborationDetailRoute)["useNavigate"]>;
  const { tab: activeTab } = useSearch({ strict: false }) as ReturnType<
    (typeof openCollaborationDetailRoute)["useSearch"]
  >;
  const merchant = useMerchant();
  const [breakdownFilter, setBreakdownFilter] = useState<string | null>(null);
  const newCollabsGroupsEnabled = useFeatureFlag("F-new-collabs-groups");
  const goBackToCollaborations = () => {
    navigate({
      to: newCollabsGroupsEnabled ? "/merchant/$merchantId/collabs/groups/open" : "/merchant/$merchantId/collabs/open",
      params: { merchantId: merchant },
    });
  };
  const setActiveTab = (next: CreatorStatusTab) => {
    navigate({ search: (prev) => ({ ...prev, tab: next as typeof activeTab }) });
  };

  const { data: collaboration, isLoading, error } = useOpenCollaborationDetail(collaborationId);
  const queryClient = useQueryClient();
  const scope = useScopedMerchantClient();

  // Once the detail resolves, prefetch the first page of creators for every non-empty tab.
  // The prefetch uses the exact same queryKey + queryFn as useOpenCollaborationCreators, so
  // clicking a tab with count > 0 resolves instantly from cache.
  useEffect(() => {
    if (!collaboration || !scope.merchantId || !collaborationId) return;

    const { statusCounts: counts } = collaboration;
    const tabsToPreload: Array<[string, number]> = [
      ["sample_requested", counts.sampleRequested],
      ["sample_status", counts.sampleStatus],
      ["pending_post", counts.pendingPost],
      ["posted_no_gmv", counts.postedNoGmv],
      ["generated_gmv", counts.generatedGmv],
      ["cancelled", counts.cancelled],
    ];

    for (const [status, tabCount] of tabsToPreload) {
      if (tabCount <= 0) continue;
      const opts = { status: status as "sample_requested", pagination: { limit: 20, offset: 0 } };
      queryClient.prefetchQuery({
        queryKey: openCollaborationCreatorsKey(scope, collaborationId, opts),
        queryFn: () => fetchOpenCollaborationCreators(scope, collaborationId, opts),
      });
    }
  }, [collaboration?.collaborationId, scope, collaborationId, queryClient]);

  // Get status counts from the collaboration data
  const statusCounts = collaboration?.statusCounts ?? {
    all: 0,
    sampleRequested: 0,
    sampleStatus: 0,
    pendingPost: 0,
    generatedGmv: 0,
    postedNoGmv: 0,
    cancelled: 0,
  };

  const tabs = [
    {
      id: "all" as const,
      label: "All Creators",
      badge: statusCounts.all,
    },
    {
      id: "sample_requested" as const,
      label: "Sample Requested",
      badge: statusCounts.sampleRequested,
    },
    {
      id: "sample_status" as const,
      label: "Sample status",
      badge: statusCounts.sampleStatus,
    },
    {
      id: "pending_post" as const,
      label: "Pending Post",
      badge: statusCounts.pendingPost,
    },
    {
      id: "posted_no_gmv" as const,
      label: "Posted No GMV",
      badge: statusCounts.postedNoGmv,
    },
    {
      id: "generated_gmv" as const,
      label: "Generated GMV",
      badge: statusCounts.generatedGmv,
    },
    {
      id: "cancelled" as const,
      label: "Cancelled",
      badge: statusCounts.cancelled,
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <LoadingIndicator label="Loading collaboration..." size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !collaboration) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <EmptyState size="md">
          <div className="pb-6">
            <EmptyState.FeaturedIcon color="gray" />
          </div>
          <EmptyState.Content>
            <EmptyState.Title>Collaboration not found</EmptyState.Title>
            <EmptyState.Description>
              The collaboration you're looking for doesn't exist or you don't have access to it.
            </EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button color="secondary" size="md" onClick={goBackToCollaborations}>
              <div className="flex items-center">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Collaborations
              </div>
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  const handleTabChange = (key: string | number) => {
    navigate({ search: (prev) => ({ ...prev, tab: key as typeof activeTab }) });
  };

  return (
    <div className="flex flex-col w-full min-h-screen items-center bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col px-16 pt-8 pb-8`}>
        {/* Back button */}
        <Button
          color="tertiary"
          size="sm"
          onClick={goBackToCollaborations}
          className="text-quaternary hover:text-primary pl-0 w-fit mb-3"
        >
          <div className="flex items-center">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Collaborations
          </div>
        </Button>

        {/* Header with collaboration info */}
        <OpenCollaborationDetailHeader
          collaboration={collaboration}
          sampleRequestedCount={statusCounts.sampleRequested}
        />

        {/* Collaboration Insights Panel — between header and creator tabs */}
        <CollaborationInsightsPanel
          collaborationId={collaborationId}
          collaborationType="open"
          onFilterChange={setActiveTab}
          onBreakdownFilterChange={setBreakdownFilter}
        />

        {/* Status filter tabs */}
        <div id="collaboration-creators-list" className="mb-6 scroll-mt-16">
          <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange}>
            <Tabs.List type="underline" items={tabs}>
              {(tab) => <Tabs.Item {...tab} />}
            </Tabs.List>
          </Tabs>
        </div>

        {/* Creators table */}
        <CollaborationCreatorsTable
          collaborationId={collaborationId}
          collaborationName={collaboration.productTitle}
          statusFilter={activeTab}
          collaborationType="open"
          breakdownFilter={breakdownFilter}
          onClearBreakdownFilter={() => setBreakdownFilter(null)}
        />
      </div>
    </div>
  );
};
